import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Archive,
  ArrowLeft,
  Plus,
  Trash,
  UploadSimple,
  FileText,
  Receipt,
  Briefcase,
  CheckSquare,
  Square,
  PencilSimple,
  Stack,
  Calculator,
  Hammer,
  DotsThreeVertical,
  ArrowsClockwise,
  CalendarBlank,
  User,
  Phone,
  MapPin,
  CheckCircle,
  ArrowRight,
  Copy,
  ChatCircle,
  EnvelopeSimple,
  Printer,
  ChatText,
  Clock,
  Warning,
  SquaresFour,
  ListChecks,
  Package,
  Drop,
  FolderOpen,
  Wrench,
  CurrencyDollar,
} from "@phosphor-icons/react";
import JobDetail from "@/pages/JobDetail";
import { WorkflowDialog } from "@/components/WorkflowDialog";
import { SegTabs, useSwipeTabs } from "@/components/SegTabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AppHeader } from "@/components/AppHeader";
import { AddressCombo } from "@/components/AddressCombo";
import { Button } from "@/components/ui/button";
import { MoveAccountDialog } from "@/components/MoveAccountDialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DesignerPlan } from "@/components/CountertopDesigner";
import { TeamNotes, PinnedNote } from "@/components/TeamNotes";
import { LienWaiverDialog } from "@/components/LienWaiverDialog";
import LabelDialog from "@/components/LabelDialog";
import { DateField } from "@/components/DateField";
import {
  DateField as SchedDate,
  TimeField as SchedTime,
} from "@/components/DateTimeField";
import { fmtTime12 } from "@/lib/scheduleWindows";
import { PortalThread } from "@/components/PortalThread";
import { JobEmailThread } from "@/components/JobEmailThread";
import { ProjectOverview } from "@/components/ProjectOverview";
import { ProjectExtraTabs } from "@/components/ProjectExtraTabs";
import { ConfirmTextDialog } from "@/components/ConfirmTextDialog";
import { TaskDialog } from "@/components/TaskDialog";
import { ExtraStopCard } from "@/components/ExtraStopCard";
import { FixturesPanel } from "@/components/FixturesPanel";
import { AcceptDialog, QuoteSignoff } from "@/components/ProjectDealActions";
import { ProjectNextSteps } from "@/components/ProjectNextSteps";
import { ProjectTimeline } from "@/components/ProjectTimeline";
import { FinanceLedger } from "@/components/FinanceLedger";
import { ProjectMaterials } from "@/components/ProjectMaterials";
import { ProjectMaterialNeeds } from "@/components/ProjectMaterialNeeds";
import { HOLDS, HoldBadge } from "@/components/HoldBadge";
import { AdvanceStageDialog } from "@/components/AdvanceStageDialog";
import { InstallBadge } from "@/pages/Projects";
import { advanceChecks, nextSteps, tabCounts } from "@/lib/projectNextSteps";
import { smsHref } from "@/lib/sms";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { crewLabel } from "@/lib/permissions";

const STAGES = [
  "Lead",
  "Quoted",
  "Approved",
  "Template",
  "Fabrication",
  "Install",
  "Complete",
  "Lost",
];

const STAGE_DOT = {
  Lead: "bg-zinc-400",
  Quoted: "bg-blue-400",
  Approved: "bg-indigo-400",
  Template: "bg-amber-400",
  Fabrication: "bg-orange-400",
  Install: "bg-cyan-400",
  Complete: "bg-emerald-400",
  Lost: "bg-red-400",
};

const WINDOWS = ["All day", "AM", "PM"];

const STOPS = [
  ["Template", "template_date", "template_window", "template_time", "template_crew", "template_areas"],
  ["Fabrication", "fab_date", "fab_window", "fab_time", "fab_crew", "fab_areas"],
  ["Install", "install_date", "install_window", "install_time", "install_crew", "install_areas"],
];

const STOP_DOT = {
  Template: "#c084fc",
  Fabrication: "#facc15",
  Install: "#60a5fa",
};

const PTABS = [
  { key: "overview", label: "Overview", Icon: SquaresFour },
  { key: "field", label: "Field work", Icon: Wrench },
  { key: "schedule", label: "Schedule", Icon: CalendarBlank },
  { key: "tasks", label: "Tasks", Icon: CheckSquare },
  { key: "checklist", label: "Checklist", Icon: ListChecks },
  { key: "special", label: "Special orders", Icon: Package },
  { key: "fixtures", label: "Materials", Icon: Drop },
  { key: "work", label: "Quotes & Jobs", Icon: Receipt },
  { key: "finances", label: "Finances", Icon: CurrencyDollar },
  { key: "files", label: "Drawings & Files", Icon: FolderOpen },
  { key: "comms", label: "Comms", Icon: ChatText },
];
const TAB_KEYS = new Set(PTABS.map((t) => t.key));

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const day = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

const Card = ({ title, icon: Icon, children, testid, action, show = true }) => {
  if (!show) return null;
  return (
    <section
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} weight="bold" className="text-indigo-300" />}
        <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-300">
          {title}
        </h2>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [p, setP] = useState(null);
  const [taskTypes, setTaskTypes] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [wfJob, setWfJob] = useState(null);
  const [taskDialog, setTaskDialog] = useState({ open: false, task: null });
  const [stageDialog, setStageDialog] = useState({ open: false, jobId: "", task: null });
  const [pool, setPool] = useState({ quotes: [], jobs: [] });
  const [invoices, setInvoices] = useState([]);
  const [editHeader, setEditHeader] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [acctAddresses, setAcctAddresses] = useState([]);
  useEffect(() => {
    if (!editHeader || !p?.customer_id) return;
    api
      .get(`/customers/${p.customer_id}`)
      .then(({ data }) => setAcctAddresses([data.address, ...(data.job_addresses || [])]))
      .catch(() => {});
  }, [editHeader, p?.customer_id]);
  const [busy, setBusy] = useState(false);
  const [msgTab, setMsgTab] = useState(params.get("notes") === "1" ? "notes" : "portal");
  const [lienOpen, setLienOpen] = useState(false);
  const urlTab = params.get("tab");
  const tab = TAB_KEYS.has(urlTab) ? urlTab : "overview";
  const setTab = (key) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === "overview") next.delete("tab");
        else next.set("tab", key);
        return next;
      },
      { replace: true },
    );
  const swipe = useSwipeTabs(PTABS, tab, (t) => setTab(t.key));
  const [signoff, setSignoff] = useState(null); // {quote, mode}
  const [apptBusy, setApptBusy] = useState("");
  const [labelDlg, setLabelDlg] = useState(null);
  const [apptConfirm, setApptConfirm] = useState(null); // {jobId, kind, to, message}
  const [apptEmail, setApptEmail] = useState(null); // {jobId, kind, to, message}
  const [conflicts, setConflicts] = useState([]);
  const [advance, setAdvance] = useState(null); // {stage, checks}
  const [needs, setNeeds] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}`);
    if (data?.id && data.id !== id) {
      navigate(`/projects/${data.id}${window.location.search}`, { replace: true });
      return;
    }
    setP(data);
    const res = await api.get("/invoices", { params: { project_id: id } });
    setInvoices(res.data.invoices || []);
    api.get(`/projects/${id}/material-needs`).then((r) => setNeeds(r.data)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    load();
    api
      .get(`/projects/${id}/unassigned`)
      .then(({ data }) => setPool(data))
      .catch(() => {});
  }, [id, load]);

  useEffect(() => {
    api
      .get("/settings/task-types")
      .then(({ data }) => setTaskTypes(data.task_types || []))
      .catch(() => {});
    api
      .get("/crew-list")
      .then(({ data }) => setCrewList(data || []))
      .catch(() => {});
    api
      .get("/schedule/crew-conflicts")
      .then(({ data }) => setConflicts(data.enabled ? data.conflicts || [] : []))
      .catch(() => {});
  }, []);

  const patch = async (body, quiet) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/projects/${id}`, body);
      setP(data);
      if (!quiet) toast.success("Project saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save the project",
      );
    }
    setBusy(false);
  };

  const attach = async (body) => {
    const { data } = await api.post(`/projects/${id}/attach`, body);
    setP(data);
    const res = await api.get(`/projects/${id}/unassigned`);
    setPool(res.data);
    toast.success("Added to the project");
  };

  const detach = async (body) => {
    const { data } = await api.post(`/projects/${id}/detach`, body);
    setP(data);
    const res = await api.get(`/projects/${id}/unassigned`);
    setPool(res.data);
  };

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/projects/${id}/files`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File added");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Upload failed",
      );
    }
  };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const closeProject = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/close`);
      setP(data);
      toast.success("Project closed (Complete + archived)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not close project");
    }
  };
  const removeProject = async (closeFirst = false) => {
    setDeleting(true);
    try {
      if (closeFirst) await api.post(`/projects/${id}/close?force=true`);
      await api.delete(`/projects/${id}`);
    } catch (e) {
      setDeleting(false);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete project");
      return;
    }
    setDeleting(false);
    setDeleteOpen(false);
    toast.success(closeFirst ? "Project closed and deleted" : "Project deleted", {
      duration: 8000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            await api.post(`/projects/${id}/restore`);
            toast.success("Project restored");
            navigate(`/projects/${id}`);
          } catch (e) {
            toast.error("Could not restore project");
          }
        },
      },
    });
    navigate("/projects?tab=list");
  };

  const regenerateInvoices = async () => {
    if (!window.confirm("Delete this project's unpaid invoices and raise fresh ones from its accepted quotes? Invoices that already took money are kept."))
      return;
    try {
      const { data } = await api.post(`/projects/${id}/invoices/regenerate`);
      toast.success(`Removed ${data.removed}, created ${data.created}${data.kept_paid ? `, kept ${data.kept_paid} paid` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate invoices");
    }
  };

  const newQuote = () =>
    navigate(
      `/quotes/new?project=${id}&customer=${p?.customer_id || ""}` +
        `&address=${encodeURIComponent(p?.job_address || "")}` +
        `&name=${encodeURIComponent(p?.name || "")}`,
    );

  const saveHeader = async () => {
    await patch({ name: p.name, job_address: p.job_address, job_duration_min: p.job_duration_min || 0 });
    setEditHeader(false);
  };

  if (!p)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Project" />
      </div>
    );

  const openAddTask = () => setTaskDialog({ open: true, task: null });
  const openEditTask = (t) => setTaskDialog({ open: true, task: t });

  const toggleTaskDone = async (taskId) => {
    try {
      const { data } = await api.patch(`/projects/${id}/tasks/${taskId}/done`);
      setP(data);
    } catch {
      toast.error("Could not update the task");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const { data } = await api.delete(`/projects/${id}/tasks/${taskId}`);
      setP(data);
      toast.success("Task removed");
    } catch {
      toast.error("Could not remove the task");
    }
  };

  const patchJob = async (jobId, body) => {
    try {
      await api.patch(`/jobs/${jobId}`, body);
      await load();
      toast.success("Schedule updated");
    } catch {
      toast.error("Could not update the job schedule");
    }
  };
  const toggleStopArea = (j, areasKey, name) => {
    const cur = j[areasKey] || [];
    patchJob(j.id, {
      [areasKey]: cur.includes(name) ? cur.filter((a) => a !== name) : [...cur, name],
    });
  };
  const setArchived = async (archived) => {
    try {
      const { data } = await api.patch(`/projects/${id}`, { archived });
      setP(data);
      toast.success(archived ? "Project archived" : "Project restored");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not archive");
    }
  };

  const pushDates = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/push-dates`);
      setP(data);
      const withInstall = (data.jobs || []).filter((j) => j.install_date);
      if (withInstall.length > 0) {
        toast.success("Dates pushed to every job", {
          action: {
            label: `Text ${withInstall.length} customer${withInstall.length > 1 ? "s" : ""}`,
            onClick: () => notifyPushed(withInstall),
          },
        });
      } else {
        toast.success("Dates pushed to every job");
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not push the dates",
      );
    }
  };

  const notifyPushed = async (jobs) => {
    let sent = 0;
    let failed = 0;
    for (const j of jobs) {
      try {
        await api.post(`/jobs/${j.id}/appointment-text`, { kind: "Install" });
        sent += 1;
      } catch {
        failed += 1;
      }
    }
    if (sent)
      toast.success(
        `Texted ${sent} customer${sent > 1 ? "s" : ""} their install date`,
      );
    if (failed)
      toast.error(
        `${failed} couldn't be texted — missing phone or SMS is off`,
      );
  };

  const openApptText = async (jobId, kind, stopId = "") => {
    const key = `${jobId}-${stopId || kind}`;
    setApptBusy(key);
    try {
      const { data } = await api.get(
        `/jobs/${jobId}/appointment-text/preview`,
        { params: { kind, stop_id: stopId } },
      );
      setApptConfirm({ jobId, kind, stopId, to: data.to, message: data.message });
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Could not load the appointment text",
      );
    } finally {
      setApptBusy("");
    }
  };

  const deleteStage = async (jobId, sid) => {
    try {
      await api.delete(`/jobs/${jobId}/extra-stops/${sid}`);
      await load();
      toast.success("Stage removed");
    } catch {
      toast.error("Could not remove the stage");
    }
  };

  const doSendApptText = async (message) => {
    if (!apptConfirm) return;
    const key = `${apptConfirm.jobId}-${apptConfirm.stopId || apptConfirm.kind}`;
    setApptBusy(key);
    try {
      const { data } = await api.post(
        `/jobs/${apptConfirm.jobId}/appointment-text`,
        { kind: apptConfirm.kind, message, stop_id: apptConfirm.stopId || "" },
      );
      toast.success(`Appointment text sent to ${data.to}`);
      setApptConfirm(null);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Could not send the appointment text",
      );
    } finally {
      setApptBusy("");
    }
  };

  const openApptEmail = async (jobId, kind, stopId = "") => {
    const key = `email-${jobId}-${stopId || kind}`;
    setApptBusy(key);
    try {
      const { data } = await api.get(`/jobs/${jobId}/appointment-email/preview`, {
        params: { kind, stop_id: stopId },
      });
      setApptEmail({ jobId, kind, stopId, to: data.to, message: data.message });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load the appointment email");
    } finally {
      setApptBusy("");
    }
  };

  const doSendApptEmail = async (message) => {
    if (!apptEmail) return;
    const key = `email-${apptEmail.jobId}-${apptEmail.stopId || apptEmail.kind}`;
    setApptBusy(key);
    try {
      const { data } = await api.post(`/jobs/${apptEmail.jobId}/appointment-email`, {
        kind: apptEmail.kind,
        message,
        stop_id: apptEmail.stopId || "",
      });
      toast.success(`Appointment email sent to ${data.to}`);
      setApptEmail(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the appointment email");
    } finally {
      setApptBusy("");
    }
  };

  const live = invoices.reduce(
    (acc, i) =>
      i.status === "Void"
        ? acc
        : { invoiced: acc.invoiced + i.amount, paid: acc.paid + i.paid },
    { invoiced: 0, paid: 0 },
  );

  const sourceQuote =
    (p.quotes?.length ? p.quotes[p.quotes.length - 1] : null) ||
    (p.deal?.quote_id
      ? { id: p.deal.quote_id, code: p.deal.quote_code }
      : null);

  const todayIso = new Date().toISOString().slice(0, 10);
  const nextStop = (p.jobs || [])
    .flatMap((j) =>
      STOPS.map(([label, dateKey, windowKey, timeKey, crewKey]) => ({
        job: j,
        label,
        date: j[dateKey],
        window: j[windowKey],
        time: j[timeKey],
        crew: j[crewKey],
      })),
    )
    .filter((s) => s.date && s.date >= todayIso)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))[0];

  const conflictKeys = new Set();
  conflicts.forEach((c) =>
    (c.stops || []).forEach((s) => conflictKeys.add(`${s.job_id}-${s.kind}`)),
  );

  const stageFlow = [
    "Lead",
    "Quoted",
    "Approved",
    "Template",
    "Fabrication",
    "Install",
    "Complete",
  ];
  const stageIdx = stageFlow.indexOf(p.stage);
  const nextStage =
    stageIdx >= 0 && stageIdx < stageFlow.length - 1
      ? stageFlow[stageIdx + 1]
      : null;
  const stepCtx = { p, invoices, conflicts };
  const areaNames = [
    ...new Set(
      ((p.quotes || []).filter((q) => q.status === "Accepted").length
        ? p.quotes.filter((q) => q.status === "Accepted")
        : p.quotes || []
      ).flatMap((q) => q.areas || []),
    ),
  ];
  const balanceDue = invoices
    .filter((i) => !["Void", "Draft"].includes(i.status))
    .reduce((n, i) => n + Number(i.balance || 0), 0);
  const openJobs = (p.jobs || []).filter(
    (j) => !j.archived && !["Installed", "Complete", "Completed"].includes(j.stage),
  ).length;
  const archiveBlockers = [
    p.stage !== "Complete" && "Complete",
    balanceDue > 0.009 && "paid",
    openJobs > 0 && `${openJobs} job${openJobs > 1 ? "s" : ""} closed`,
  ].filter(Boolean);
  const canArchive = p.stage === "Complete" && balanceDue <= 0.009 && openJobs === 0;
  const steps = nextSteps(stepCtx);
  const counts = tabCounts(p, conflicts);
  const advanceStage = () => {
    if (!nextStage) return;
    const checks = advanceChecks(nextStage, stepCtx);
    if (checks.some((c) => !c.ok)) setAdvance({ stage: nextStage, checks });
    else patch({ stage: nextStage });
  };
  const confirmAdvance = async () => {
    if (!advance) return;
    await patch({ stage: advance.stage });
    setAdvance(null);
  };
  const goStep = (s) => {
    const g = s.go || {};
    if (g.tab) return setTab(g.tab);
    if (g.action === "newQuote") return newQuote();
    if (g.action === "newJob")
      return navigate(`/projects/new?customer=${p.customer_id}&project=${p.id}`);
    if (g.action === "customer") return navigate(`/customers/${p.customer_id}`);
    if (g.action === "account") return navigate("/customers");
    if (g.action === "invoice") return navigate(`/invoices/${g.id}`);
    if (g.action === "slabs") return navigate("/inventory/slabs");
    if (g.action === "hold") return patch({ hold_status: "", hold_note: "" });
    return undefined;
  };
  const copyAddr = () =>
    navigator.clipboard?.writeText(p.job_address || "").then(
      () => toast.success("Address copied"),
      () => toast.error("Could not copy"),
    );
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    p.job_address || "",
  )}`;
  const quickBtn =
    "inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200";

  return (
    <div className="grain min-h-screen">
      <AppHeader title={p.name} />
      <main className="relative z-10 pb-28 pt-6">
        <button
          type="button"
          data-testid="project-back"
          onClick={() => navigate("/projects?tab=list")}
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        >
          <ArrowLeft size={13} weight="bold" /> Projects
        </button>

        <div
          className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="project-header-card"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 font-mono-data text-xs uppercase tracking-widest text-zinc-500">
                {p.code}
                {sourceQuote?.id && (
                  <Link
                    to={`/quotes/${sourceQuote.id}/design`}
                    data-testid="project-source-quote-link"
                    className="inline-flex items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-indigo-300 transition-colors duration-150 hover:bg-indigo-500/20"
                  >
                    <Receipt size={11} weight="bold" /> {sourceQuote.code}
                  </Link>
                )}
                <InstallBadge p={p} />
                <HoldBadge status={p.hold_status} since={p.hold_since} note={p.hold_note} testId="project-hold-badge" />
                {p.archived && (
                  <span
                    data-testid="project-archived-badge"
                    className="inline-flex items-center gap-1 rounded-full border border-zinc-600 bg-zinc-800 px-2 py-0.5 text-zinc-300"
                  >
                    <Archive size={11} weight="bold" /> Archived
                  </span>
                )}
              </div>
              {editHeader ? (
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    data-testid="project-name-input"
                    value={p.name}
                    autoFocus
                    onChange={(e) => setP({ ...p, name: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveHeader();
                    }}
                    className="h-11 border-zinc-800 bg-zinc-950 text-xl font-bold"
                  />
                  <Button
                    data-testid="project-name-save"
                    disabled={busy}
                    onClick={saveHeader}
                    className="h-11 shrink-0 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                  >
                    Save
                  </Button>
                </div>
              ) : (
                <h1
                  data-testid="project-name-text"
                  className="mt-1 break-words text-2xl font-bold tracking-tight sm:truncate sm:text-3xl"
                >
                  {p.name}
                </h1>
              )}
              <p className="mt-1 text-sm font-semibold">
                {p.customer_id ? (
                  <Link
                    to={`/customers/${p.customer_id}`}
                    className="text-indigo-300 transition-colors duration-150 hover:text-indigo-200"
                    data-testid="project-account-link"
                  >
                    {p.customer_company || p.customer_name || "Account"}
                  </Link>
                ) : (
                  <span className="text-zinc-400">No account linked</span>
                )}
              </p>
              {p.customer_company &&
                p.customer_contact &&
                p.customer_contact !== p.customer_company && (
                  <p
                    data-testid="project-contact-text"
                    className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-zinc-300"
                  >
                    <User
                      size={14}
                      weight="bold"
                      className="shrink-0 text-zinc-500"
                    />
                    {p.customer_contact}
                  </p>
                )}
              {editHeader ? (
                <AddressCombo
                  testId="project-address"
                  className="mt-2"
                  value={p.job_address || ""}
                  onChange={(v) => setP({ ...p, job_address: v })}
                  options={acctAddresses}
                  placeholder="Job address"
                />
              ) : (
                <p
                  data-testid="project-address-text"
                  className="mt-2 text-sm text-zinc-400"
                >
                  {p.job_address || "No job address yet"}
                </p>
              )}
              <PinnedNote p={p} />
              {editHeader ? (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="project-duration-edit">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Job duration
                  </span>
                  {[["½ day", 240], ["Full day", 480]].map(([lbl, mins]) => (
                    <button
                      key={mins}
                      type="button"
                      data-testid={`project-duration-preset-${mins}`}
                      onClick={() => setP({ ...p, job_duration_min: mins })}
                      className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                        (p.job_duration_min || 0) === mins
                          ? "border-indigo-500 bg-indigo-500/15 text-indigo-200"
                          : "border-zinc-700 text-zinc-300 hover:border-indigo-500"
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                  <input
                    type="number"
                    min={0}
                    step={15}
                    value={p.job_duration_min || 0}
                    onChange={(e) =>
                      setP({ ...p, job_duration_min: Math.max(0, Number(e.target.value) || 0) })
                    }
                    data-testid="project-duration-input"
                    className="h-8 w-20 rounded-md border border-zinc-800 bg-zinc-950 px-2 text-right text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none"
                  />
                  <span className="text-xs text-zinc-500">min (custom) — used for route ETAs</span>
                </div>
              ) : p.job_duration_min ? (
                <p className="mt-1 text-xs text-zinc-500" data-testid="project-duration-text">
                  Est. {Math.floor(p.job_duration_min / 60) > 0 ? `${Math.floor(p.job_duration_min / 60)}h ` : ""}
                  {p.job_duration_min % 60}m on site — used for route ETAs
                </p>
              ) : null}
              {lienOpen && <LienWaiverDialog open onClose={() => setLienOpen(false)} p={p} onSaved={load} />}
              <MoveAccountDialog
                open={moveOpen}
                onClose={() => setMoveOpen(false)}
                onDone={load}
                endpoint={`/projects/${id}/move-account`}
                title="Move project to another account"
                description="Moves this project with its job, quotes and invoices."
                confirmLabel="Move project"
                excludeId={p.customer_id}
              />
              <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent data-testid="project-delete-dialog" className="bg-zinc-950 border-zinc-800">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {p.code || "this project"}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      The project is removed from your lists. Its quotes, jobs and invoices stay put.
                      You can undo from the toast right after.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="project-delete-cancel">Cancel</AlertDialogCancel>
                    <Button
                      variant="outline"
                      data-testid="project-close-delete-confirm"
                      disabled={deleting}
                      onClick={() => removeProject(true)}
                    >
                      Close &amp; delete
                    </Button>
                    <Button
                      data-testid="project-delete-confirm"
                      disabled={deleting}
                      className="bg-red-600 hover:bg-red-500 text-white"
                      onClick={() => removeProject(false)}
                    >
                      {deleting ? "Deleting…" : "Delete project"}
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <div className="flex min-w-0 flex-wrap items-start gap-2 sm:shrink-0 sm:justify-end">
              <div className="flex min-w-0 flex-col items-start gap-1.5 sm:items-end">
                <Select
                  value={p.stage}
                  onValueChange={(v) => {
                    if (v === "__not_waiting")
                      return patch({ hold_status: "", hold_note: "" });
                    if (HOLDS.includes(v))
                      return patch({ hold_status: v, hold_note: p.hold_note || "" });
                    patch({ stage: v });
                  }}
                >
                  <SelectTrigger
                    data-testid="project-stage-select"
                    className="h-9 w-auto gap-2 border-zinc-800 bg-zinc-950 pl-3 pr-2 text-xs font-semibold uppercase tracking-wider"
                  >
                    <span
                      className={`h-2.5 w-2.5 shrink-0 rounded-full ${STAGE_DOT[p.stage] || STAGE_DOT.Lead}`}
                    />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Stage</SelectLabel>
                      {STAGES.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`project-stage-opt-${s.toLowerCase()}`}
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Waiting on</SelectLabel>
                      <SelectItem
                        value="__not_waiting"
                        data-testid="project-hold-opt-none"
                      >
                        Not waiting
                      </SelectItem>
                      {HOLDS.map((h) => (
                        <SelectItem
                          key={h}
                          value={h}
                          data-testid={`project-hold-opt-${h.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {h}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {!p.stage_manual && (
                  <span
                    data-testid="project-stage-auto"
                    className="text-[10px] uppercase tracking-widest text-zinc-600"
                  >
                    Auto from quotes &amp; jobs
                  </span>
                )}
                {p.hold_status && (
                  <div
                    className="flex flex-wrap items-center gap-2"
                    data-testid="project-hold-active"
                  >
                    <HoldBadge
                      status={p.hold_status}
                      note={p.hold_note}
                      testId="project-hold-badge-inline"
                    />
                    <input
                      data-testid="project-hold-note"
                      defaultValue={p.hold_note || ""}
                      key={`${p.hold_status}-${p.hold_note || ""}`}
                      placeholder="What are we waiting on?"
                      disabled={busy}
                      onBlur={(e) =>
                        e.target.value !== (p.hold_note || "") &&
                        patch({ hold_status: p.hold_status, hold_note: e.target.value })
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" && e.currentTarget.blur()
                      }
                      className="h-9 w-full min-w-0 rounded-full border border-zinc-700 bg-zinc-950 px-3 text-xs text-zinc-200 outline-none placeholder:text-zinc-600 focus:border-amber-500/50 sm:w-56"
                    />
                    {p.hold_status === "Waiting on Customer" && (
                      <>
                        {["Awaiting deposit", "Awaiting sink selection", "Awaiting sign-off", "Awaiting measurements"].map((r) => (
                          <button
                            key={r}
                            type="button"
                            data-testid={`project-hold-reason-${r.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => patch({ hold_status: p.hold_status, hold_note: r })}
                            className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                              p.hold_note === r
                                ? "border-amber-500/50 bg-amber-500/15 text-amber-200"
                                : "border-zinc-700 text-zinc-400 hover:border-amber-500/40 hover:text-amber-200"
                            }`}
                          >
                            {r}
                          </button>
                        ))}
                        <span className="flex items-center gap-1.5" data-testid="project-hold-followup">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                            Follow up
                          </span>
                          <DateField
                            testid="project-hold-followup-date"
                            value={p.hold_follow_up || ""}
                            onChange={(v) => patch({ hold_follow_up: v })}
                          />
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="project-actions-menu"
                    title="Project actions"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-300 transition-colors duration-150 hover:border-zinc-700"
                  >
                    <DotsThreeVertical size={20} weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  data-testid="project-actions-menu-content"
                  className="w-56 border-zinc-800 bg-zinc-900"
                >
                  <DropdownMenuItem
                    data-testid="menu-project-quote"
                    onClick={newQuote}
                  >
                    <Calculator size={15} weight="bold" className="mr-2" /> New
                    quote
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-project-job"
                    onClick={() =>
                      navigate(
                        `/projects/new?customer=${p.customer_id}&project=${p.id}`,
                      )
                    }
                  >
                    <Hammer size={15} weight="bold" className="mr-2" /> New job
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-project-invoices"
                    onClick={() => navigate("/invoices")}
                  >
                    <Receipt size={15} weight="bold" className="mr-2" /> Invoices
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-project-regenerate-invoices"
                    onClick={regenerateInvoices}
                  >
                    <ArrowsClockwise size={15} weight="bold" className="mr-2" /> Regenerate invoices from quotes
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-project-move-account"
                    onClick={() => setMoveOpen(true)}
                  >
                    <ArrowsClockwise size={15} weight="bold" className="mr-2" /> Move to another account
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-project-edit"
                    onClick={() => setEditHeader(true)}
                  >
                    <PencilSimple size={15} weight="bold" className="mr-2" />{" "}
                    Rename project
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-project-lien-waiver"
                    onClick={() => setLienOpen(true)}
                  >
                    <FileText size={15} weight="bold" className="mr-2" /> Lien waiver PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-project-archive"
                    disabled={!p.archived && !canArchive}
                    title={p.archived ? "" : "Available once the project is Complete, fully paid, and all jobs are closed"}
                    onClick={() => setArchived(!p.archived)}
                  >
                    <Archive size={15} weight="bold" className="mr-2" />
                    {p.archived
                      ? "Restore from archive"
                      : canArchive
                        ? "Archive project"
                        : `Archive (needs ${archiveBlockers.join(" + ")})`}
                  </DropdownMenuItem>
                  {!p.archived && (
                    <DropdownMenuItem
                      data-testid="menu-project-close"
                      disabled={openJobs > 0}
                      onClick={closeProject}
                    >
                      <CheckCircle size={15} weight="bold" className="mr-2" /> Close project
                      (Complete + archive)
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem
                    data-testid="menu-project-delete"
                    onSelect={() => setDeleteOpen(true)}
                    className="text-red-400 focus:text-red-300"
                  >
                    <Trash size={15} weight="bold" className="mr-2" /> Delete
                    project
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {(p.fixtures || []).length > 0 &&
            (() => {
              const total = p.fixtures.length;
              const done = p.fixtures.filter((f) => f.confirmed).length;
              const pct = Math.round((done / total) * 100);
              return (
                <div className="mt-4" data-testid="project-fixtures-progress">
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                    <span>Cutouts confirmed</span>
                    <span data-testid="project-fixtures-count">
                      {done} of {total}
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-zinc-800">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        done === total ? "bg-emerald-500" : "bg-indigo-500"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })()}
          <div
            className="mt-4 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-4"
            data-testid="project-quick-actions"
          >
            {p.customer_phone && (
              <>
                <a
                  href={`tel:${p.customer_phone}`}
                  data-testid="quick-call"
                  className={quickBtn}
                >
                  <Phone size={13} weight="bold" /> Call
                </a>
                <a
                  href={smsHref(p.customer_phone)}
                  data-testid="quick-text"
                  className={quickBtn}
                >
                  <ChatText size={13} weight="bold" /> Text
                </a>
              </>
            )}
            {p.job_address && (
              <>
                <button
                  type="button"
                  data-testid="quick-copy-address"
                  onClick={copyAddr}
                  className={quickBtn}
                >
                  <Copy size={13} weight="bold" /> Copy address
                </button>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="quick-maps"
                  className={quickBtn}
                >
                  <MapPin size={13} weight="bold" /> Maps
                </a>
              </>
            )}
            <Link
              to={`/calendar?project=${id}`}
              data-testid="quick-calendar"
              className={quickBtn}
            >
              <CalendarBlank size={13} weight="bold" /> Calendar
            </Link>
            {nextStage && (
              <button
                type="button"
                data-testid="quick-advance-stage"
                onClick={advanceStage}
                disabled={busy}
                className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300 transition-colors duration-150 hover:bg-emerald-500/20 disabled:opacity-50"
              >
                Advance to {nextStage} <ArrowRight size={13} weight="bold" />
              </button>
            )}
          </div>
        </div>
        <AdvanceStageDialog
          open={!!advance}
          onOpenChange={(o) => !o && setAdvance(null)}
          stage={advance?.stage}
          checks={advance?.checks || []}
          onConfirm={confirmAdvance}
          busy={busy}
        />

        {nextStop && (
          <Link
            to={`/projects/${nextStop.job.id}?tab=field`}
            data-testid="project-next-up"
            className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-indigo-500/30 bg-indigo-500/[0.06] px-4 py-3 transition-colors duration-150 hover:border-indigo-500/60"
          >
            <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-300">
              Next up
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-200">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: STOP_DOT[nextStop.label] }}
              />
              {nextStop.label}
            </span>
            <span className="font-mono-data inline-flex items-center gap-1.5 text-sm text-zinc-100">
              <CalendarBlank size={14} weight="bold" /> {day(nextStop.date)}
            </span>
            <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-300">
              {nextStop.time
                ? `${fmtTime12(nextStop.time)} · ${nextStop.window}`
                : nextStop.window}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-zinc-400">
              <User size={13} weight="bold" /> {crewLabel(nextStop.crew)}
            </span>
            {nextStop.job.address && (
              <span className="inline-flex min-w-0 items-center gap-1 truncate text-xs text-zinc-400">
                <MapPin size={13} weight="bold" /> {nextStop.job.address}
              </span>
            )}
            {nextStop.job.appt_confirmed_at && (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                <CheckCircle size={11} weight="fill" /> Confirmed
              </span>
            )}
            <ArrowRight size={15} weight="bold" className="ml-auto text-zinc-600" />
          </Link>
        )}

        <SegTabs
          sticky
          className="mt-4"
          testId="project-tabs"
          value={tab}
          onChange={(t) => setTab(t.key)}
          tabs={PTABS.map((t) => ({
            ...t,
            badge: counts[t.key]?.n,
            tone: counts[t.key]?.tone,
          }))}
        />

        <div className="mt-4 min-w-0" {...swipe}>
          <div className="min-w-0 space-y-4">
            {tab === "overview" && (
              <>
                <ProjectTimeline p={p} />
                <ProjectNextSteps
                  steps={steps}
                  nextStage={nextStage}
                  onGo={goStep}
                />
                <ProjectMaterials materials={p.materials || []} project={p} onChanged={load}>
                  <ProjectMaterialNeeds project={p} needs={needs} onChange={setNeeds} />
                </ProjectMaterials>
                <ProjectOverview
                  p={p}
                  live={live}
                  invoices={invoices}
                  money={money}
                  day={day}
                  patch={patch}
                  navigate={navigate}
                  reload={load}
                />
              </>
            )}

            <ProjectExtraTabs p={p} patch={patch} tab={tab} />

            {tab === "finances" && (
              <FinanceLedger scope="projects" id={id} project={p} onChanged={load} />
            )}

            {tab === "fixtures" && (
              <FixturesPanel
                kind="projects"
                id={id}
                fixtures={p.fixtures || []}
                onUpdated={setP}
                quotedSinks={p.quoted_sinks || []}
                context={{
                  kind: "Project",
                  title: p.name,
                  customer: p.customer_name,
                  code: p.code,
                  address: p.job_address,
                  url: `${window.location.origin}/admin/projects/${id}`,
                }}
              />
            )}

            {tab === "schedule" && (
              <div className="space-y-4" data-testid="project-schedule">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">
                    Template, fab &amp; install — synced with each job
                  </div>
                  <Link
                    to={`/calendar?project=${id}`}
                    data-testid="project-schedule-calendar"
                    className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
                  >
                    <CalendarBlank size={13} weight="bold" /> View on calendar
                  </Link>
                </div>

                <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                  <div className="mb-3 text-xs font-bold uppercase tracking-widest text-zinc-300">
                    Target dates
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                        Target template
                      </span>
                      <DateField
                        testid="project-template-date"
                        value={p.target_template_date || ""}
                        placeholder="Pick a date"
                        className="mt-1"
                        onChange={(v) => {
                          setP({ ...p, target_template_date: v });
                          patch({ target_template_date: v });
                        }}
                      />
                      <div className="mt-2">
                        <SchedTime
                          testId="project-template-time"
                          value={p.target_template_time || ""}
                          placeholder="Pick a time"
                          onChange={(v) => {
                            setP({ ...p, target_template_time: v });
                            patch({ target_template_time: v });
                          }}
                        />
                      </div>
                    </label>
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                        Target install
                      </span>
                      <DateField
                        testid="project-install-date"
                        value={p.target_install_date || ""}
                        placeholder="Pick a date"
                        className="mt-1"
                        onChange={(v) => {
                          setP({ ...p, target_install_date: v });
                          patch({ target_install_date: v });
                        }}
                      />
                      <div className="mt-2">
                        <SchedTime
                          testId="project-install-time"
                          value={p.target_install_time || ""}
                          placeholder="Pick a time"
                          onChange={(v) => {
                            setP({ ...p, target_install_time: v });
                            patch({ target_install_time: v });
                          }}
                        />
                      </div>
                    </label>
                  </div>
                  <p className="mt-2 text-[10px] uppercase tracking-widest text-zinc-600">
                    Target dates set the primary job&apos;s schedule. A Workflow
                    stage flows back here only once it has both a date and a time.
                  </p>
                  {p.jobs.length > 0 &&
                    (p.target_template_date || p.target_install_date) && (
                      <button
                        type="button"
                        data-testid="project-push-dates"
                        onClick={pushDates}
                        className="mt-3 inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
                      >
                        <ArrowRight size={13} weight="bold" /> Push dates to all{" "}
                        {p.jobs.length} job{p.jobs.length === 1 ? "" : "s"}
                      </button>
                    )}
                </div>

                {p.jobs.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900 px-4 py-8 text-center">
                    <p className="text-xs text-zinc-500">
                      No jobs on this project yet — add a job to schedule
                      template, fab and install.
                    </p>
                    <button
                      type="button"
                      data-testid="schedule-new-job"
                      onClick={() =>
                        navigate(
                          `/projects/new?customer=${p.customer_id}&project=${p.id}`,
                        )
                      }
                      className="mt-3 inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
                    >
                      <Plus size={13} weight="bold" /> New job
                    </button>
                  </div>
                ) : (
                  p.jobs.map((j) => (
                    <div
                      key={j.id}
                      className="rounded-lg border border-zinc-800 bg-zinc-900"
                      data-testid={`schedule-job-${j.id}`}
                    >
                      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-800 px-4 py-3">
                        <Link
                          to={`/projects/${j.id}?tab=field`}
                          className="font-mono-data text-xs text-indigo-300 hover:underline"
                        >
                          {j.code}
                        </Link>
                        {j.appt_confirmed_at ? (
                          <span
                            data-testid={`schedule-confirmed-${j.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300"
                          >
                            <CheckCircle size={11} weight="fill" /> Confirmed
                            {j.appt_confirmed_via ? ` · ${j.appt_confirmed_via}` : ""}
                          </span>
                        ) : j.appt_text_sent ? (
                          <span
                            data-testid={`schedule-awaiting-${j.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300"
                          >
                            <Clock size={11} weight="bold" /> Awaiting reply
                          </span>
                        ) : null}
                        <span className="min-w-0 flex-1 truncate text-xs text-zinc-500">
                          {j.summary}
                        </span>
                        <button
                          type="button"
                          data-testid={`schedule-edit-${j.id}`}
                          onClick={() => setWfJob(j)}
                          className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-indigo-500 px-3 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
                        >
                          <PencilSimple size={12} weight="bold" /> Edit schedule
                        </button>
                      </div>
                      <div className="space-y-2 p-4">
                        {STOPS.map(
                          ([label, dateKey, windowKey, timeKey, crewKey, areasKey]) => {
                            const sd = j[dateKey];
                            const stime = j[timeKey];
                            const sw = j[windowKey] || "All day";
                            return (
                              <div
                                key={label}
                                data-testid={`schedule-stop-${j.id}-${label}`}
                                className="flex flex-wrap items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
                              >
                                <span className="w-24 text-xs uppercase tracking-widest text-zinc-400">
                                  {label}
                                </span>
                                <span className="font-mono-data inline-flex items-center gap-1 text-sm">
                                  <CalendarBlank size={14} /> {sd || "Not scheduled"}
                                </span>
                                {sd && (
                                  <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-300">
                                    {stime ? `${fmtTime12(stime)} · ${sw}` : sw}
                                  </span>
                                )}
                                {(j[areasKey] || []).length > 0 && (
                                  <span
                                    data-testid={`schedule-areas-summary-${j.id}-${label}`}
                                    className="truncate text-[10px] text-zinc-500"
                                  >
                                    · {(j[areasKey] || []).join(", ")}
                                  </span>
                                )}
                                {conflictKeys.has(`${j.id}-${label}`) && (
                                  <span
                                    data-testid={`schedule-conflict-${j.id}-${label}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-red-300"
                                  >
                                    <Warning size={10} weight="fill" /> Crew double-booked
                                  </span>
                                )}
                                <span className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-400">
                                  <User size={13} /> {crewLabel(j[crewKey])}
                                </span>
                                {sd && (
                                  <button
                                    type="button"
                                    data-testid={`schedule-appt-text-${j.id}-${label}`}
                                    onClick={() => openApptText(j.id, label)}
                                    disabled={apptBusy === `${j.id}-${label}`}
                                    className="inline-flex h-7 items-center gap-1 rounded-md border border-indigo-500/50 bg-indigo-500/10 px-2.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20 disabled:opacity-50"
                                  >
                                    <ChatCircle size={12} weight="bold" />
                                    {apptBusy === `${j.id}-${label}`
                                      ? "Sending\u2026"
                                      : "Text appt"}
                                  </button>
                                )}
                                {sd && (
                                  <button
                                    type="button"
                                    data-testid={`schedule-appt-email-${j.id}-${label}`}
                                    onClick={() => openApptEmail(j.id, label)}
                                    disabled={apptBusy === `email-${j.id}-${label}`}
                                    className="inline-flex h-7 items-center gap-1 rounded-md border border-emerald-500/50 bg-emerald-500/10 px-2.5 text-[10px] font-bold uppercase tracking-widest text-emerald-200 transition-colors duration-150 hover:bg-emerald-500/20 disabled:opacity-50"
                                  >
                                    <EnvelopeSimple size={12} weight="bold" />
                                    {apptBusy === `email-${j.id}-${label}`
                                      ? "Sending\u2026"
                                      : "Email appt"}
                                  </button>
                                )}
                                {(label === "Install" || label === "Template") && (
                                  <button
                                    type="button"
                                    data-testid={`schedule-${label.toLowerCase()}-label-${j.id}`}
                                    onClick={() => setLabelDlg({ jobId: j.id, kind: label })}
                                    className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                                  >
                                    <Printer size={12} weight="bold" /> Label
                                  </button>
                                )}
                              </div>
                            );
                          },
                        )}
                        {(j.extra_stops || []).length > 0 && (
                          <div className="pt-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Additional stages</div>
                        )}
                        {(j.extra_stops || []).map((st) => (
                          <ExtraStopCard
                            key={st.id}
                            jobId={j.id}
                            stop={st}
                            onChanged={() => load()}
                            onEdit={(s) => setStageDialog({ open: true, jobId: j.id, task: s })}
                            onDelete={(sid) => deleteStage(j.id, sid)}
                            onTextAppt={(s) => openApptText(j.id, s.kind, s.id)}
                            apptBusy={apptBusy === `${j.id}-${st.id}`}
                          />
                        ))}
                        <button
                          type="button"
                          data-testid={`schedule-add-stage-${j.id}`}
                          onClick={() => setStageDialog({ open: true, jobId: j.id, task: null })}
                          className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-zinc-700 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500/50 hover:text-indigo-300"
                        >
                          <Plus size={14} weight="bold" /> Add stage (template / fab / install)
                        </button>
                      </div>
                    </div>
                  ))
                )}
                <TaskDialog
                  open={stageDialog.open}
                  onOpenChange={(v) => setStageDialog((s) => ({ ...s, open: v }))}
                  resourcePath={`/jobs/${stageDialog.jobId}/extra-stops`}
                  areasSource={`/projects/${id}/drawing-areas`}
                  task={stageDialog.task}
                  taskTypes={taskTypes}
                  crewList={crewList}
                  noun="stage"
                  onSaved={() => load()}
                />
                <ConfirmTextDialog
                  open={!!apptConfirm}
                  onOpenChange={(o) => !o && setApptConfirm(null)}
                  to={apptConfirm?.to}
                  firstName={(p.customer_name || "").split(" ")[0]}
                  message={apptConfirm?.message || ""}
                  title="Appointment text — review"
                  sending={!!apptBusy}
                  onSend={doSendApptText}
                />
                <ConfirmTextDialog
                  open={!!apptEmail}
                  onOpenChange={(o) => !o && setApptEmail(null)}
                  to={apptEmail?.to}
                  firstName={(p.customer_name || "").split(" ")[0]}
                  message={apptEmail?.message || ""}
                  title="Appointment email — review"
                  sendLabel="Send email"
                  showPresets={false}
                  sending={!!apptBusy}
                  onSend={doSendApptEmail}
                />
                <WorkflowDialog
                  open={!!wfJob}
                  onOpenChange={(v) => !v && setWfJob(null)}
                  job={wfJob}
                  crewList={crewList}
                  onSaved={() => {
                    setWfJob(null);
                    load();
                  }}
                />
              </div>
            )}
            <Card
              title="The money"
              icon={Receipt}
              testid="project-money-card"
              show={false}
            >
              <div className="grid gap-3 sm:grid-cols-4">
                {[
                  ["Quoted", p.quoted_total, "quoted"],
                  ["Accepted", p.accepted_total, "accepted"],
                  ["Invoiced", live.invoiced, "invoiced"],
                  ["Paid", live.paid, "paid"],
                ].map(([label, value, key]) => (
                  <div
                    key={label}
                    data-testid={`project-money-${key}`}
                    className="rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      {label}
                    </div>
                    <div className="font-mono-data mt-1 text-lg font-bold">
                      {money(value)}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card
              title="Invoices"
              icon={Receipt}
              testid="project-invoices-card"
              show={false}
              action={
                <button
                  type="button"
                  data-testid="project-invoices-open"
                  onClick={() => navigate("/invoices")}
                  className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
                >
                  All invoices
                </button>
              }
            >
              {invoices.length === 0 ? (
                <p className="text-xs text-zinc-500">
                  No invoices yet — accepting a quote raises the deposit and
                  balance invoices automatically.
                </p>
              ) : (
                <div className="space-y-2">
                  {invoices.map((i) => (
                    <Link
                      key={i.id}
                      to={`/invoices/${i.id}`}
                      data-testid={`project-invoice-${i.id}`}
                      className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3 transition-colors duration-150 hover:border-indigo-500/50"
                    >
                      <span className="font-mono-data text-xs text-indigo-300">
                        {i.code}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {i.title}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {i.due_date
                          ? `Due ${day(i.due_date)}`
                          : "Due on install"}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                        {i.status}
                      </span>
                      <span className="font-mono-data text-sm font-bold">
                        {money(i.balance)}
                      </span>
                    </Link>
                  ))}
                  <div className="flex justify-end gap-4 border-t border-zinc-800 pt-2 text-xs text-zinc-500">
                    <span>
                      Invoiced{" "}
                      <strong className="font-mono-data text-zinc-300">
                        {money(invoices.reduce((n, i) => n + i.amount, 0))}
                      </strong>
                    </span>
                    <span>
                      Outstanding{" "}
                      <strong className="font-mono-data text-zinc-300">
                        {money(
                          invoices.reduce(
                            (n, i) => n + (i.status === "Void" ? 0 : i.balance),
                            0,
                          ),
                        )}
                      </strong>
                    </span>
                  </div>
                </div>
              )}
            </Card>

            {tab === "field" && (
              (p.jobs || []).length > 0 ? (
                <JobDetail embedded jobId={id} />
              ) : (
                <div
                  className="rounded-lg border border-dashed border-zinc-700 bg-zinc-900/60 p-8 text-center"
                  data-testid="project-field-empty"
                >
                  <Wrench size={28} weight="duotone" className="mx-auto text-indigo-300" />
                  <h3 className="mt-3 text-base font-bold text-zinc-100">No field work yet</h3>
                  <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">
                    Field work (template, fabrication, install stops, crews, photos and sign-off)
                    starts when a quote is accepted — or start it now for this project.
                  </p>
                  <Button
                    data-testid="project-start-field-work"
                    className="mt-4 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    onClick={async () => {
                      try {
                        await api.post("/jobs", {
                          project_id: id,
                          project_name: p.name || "Project",
                          customer_id: p.customer_id || null,
                          customer_name: p.customer_name || "",
                          address: p.job_address || "",
                          stage: "Template",
                        });
                        toast.success("Field work started");
                        load();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not start field work");
                      }
                    }}
                  >
                    Start field work
                  </Button>
                </div>
              )
            )}

            <Card
              title="Quotes"
              icon={Receipt}
              testid="project-quotes-card"
              show={tab === "work"}
              action={
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="project-new-quote"
                    onClick={newQuote}
                    className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
                  >
                    <Plus size={13} weight="bold" /> New quote
                  </button>
                  {pool.quotes.length > 0 && (
                    <Select
                      value=""
                      onValueChange={(v) => attach({ quote_ids: [v] })}
                    >
                      <SelectTrigger
                        data-testid="project-attach-quote"
                        className="h-9 w-44 border-zinc-800 bg-zinc-950 text-xs"
                      >
                        <SelectValue placeholder="Add a quote" />
                      </SelectTrigger>
                      <SelectContent>
                        {pool.quotes.map((q) => (
                          <SelectItem key={q.id} value={q.id}>
                            {`${q.code} · ${q.project_name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </span>
              }
            >
              {p.quotes.length === 0 && (
                <p className="text-xs text-zinc-500">
                  No quotes on this project yet.
                </p>
              )}
              <div className="space-y-2">
                {p.quotes.map((q) => (
                  <div
                    key={q.id}
                    data-testid={`project-quote-${q.id}`}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <Link
                      to={`/quotes/${q.id}`}
                      className="font-mono-data text-xs text-indigo-300 hover:underline"
                    >
                      {q.code}
                    </Link>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {q.project_name}
                      <span className="ml-2 text-xs text-zinc-500">
                        {q.slab_color}
                      </span>
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      {q.status}
                    </span>
                    <span className="font-mono-data text-sm font-bold">
                      {money(q.total)}
                    </span>
                    <Link
                      to={`/quotes/${q.id}/layout`}
                      data-testid={`project-quote-layout-${q.id}`}
                      title="Slab layout"
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:border-indigo-500 hover:text-indigo-300"
                    >
                      <Stack size={14} weight="bold" />
                    </Link>
                    <button
                      type="button"
                      data-testid={`project-detach-quote-${q.id}`}
                      onClick={() => detach({ quote_id: q.id })}
                      title="Take off this project"
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-800 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                    <div className="basis-full">
                      <QuoteSignoff
                        quote={q}
                        day={day}
                        onAccept={(qq) => setSignoff({ quote: qq, mode: "accept" })}
                        onApprove={(qq) => setSignoff({ quote: qq, mode: "drawing" })}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <AcceptDialog
                open={!!signoff}
                onClose={() => setSignoff(null)}
                project={p}
                quote={signoff?.quote || p.quotes[0]}
                mode={signoff?.mode || "accept"}
                onDone={() => load()}
              />
            </Card>

            <Card
              title="Jobs"
              icon={Briefcase}
              testid="project-jobs-card"
              show={tab === "work"}
              action={
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="project-new-job"
                    onClick={() =>
                      navigate(
                        `/projects/new?customer=${p.customer_id || ""}&project=${p.id}`,
                      )
                    }
                    className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
                  >
                    <Plus size={13} weight="bold" /> New job
                  </button>
                  {pool.jobs.length > 0 && (
                    <Select
                      value=""
                      onValueChange={(v) => attach({ job_ids: [v] })}
                    >
                      <SelectTrigger
                        data-testid="project-attach-job"
                        className="h-9 w-44 border-zinc-800 bg-zinc-950 text-xs"
                      >
                        <SelectValue placeholder="Add a job" />
                      </SelectTrigger>
                      <SelectContent>
                        {pool.jobs.map((j) => (
                          <SelectItem key={j.id} value={j.id}>
                            {`${j.code} · ${j.project_name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </span>
              }
            >
              {p.jobs.length === 0 && (
                <p className="text-xs text-zinc-500">No job yet.</p>
              )}
              <div className="space-y-2">
                {p.jobs.map((j) => (
                  <div
                    key={j.id}
                    data-testid={`project-job-${j.id}`}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <Link
                      to={`/projects/${j.id}?tab=field`}
                      className="font-mono-data text-xs text-indigo-300 hover:underline"
                    >
                      {j.code}
                    </Link>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      {j.stage}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs text-zinc-500">
                      {j.summary}
                    </span>
                    <span className="text-xs text-zinc-400">
                      Template {day(j.template_date)} · Install{" "}
                      {day(j.install_date)}
                    </span>
                    <button
                      type="button"
                      data-testid={`project-field-label-${j.id}`}
                      onClick={() => setLabelDlg({ jobId: j.id, kind: "Install" })}
                      title="Print the install 4x6 field label"
                      className="flex h-8 items-center gap-1.5 rounded-md border border-zinc-800 px-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:border-cyan-500/50 hover:text-cyan-200"
                    >
                      <Printer size={13} weight="bold" /> Label
                    </button>
                    <button
                      type="button"
                      data-testid={`project-detach-job-${j.id}`}
                      onClick={() => detach({ job_id: j.id })}
                      title="Take off this project"
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-800 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </Card>

            <Card
              title="Drawings"
              icon={Stack}
              testid="project-drawings-card"
              show={tab === "files"}
              action={
                <button
                  type="button"
                  data-testid="project-new-drawing"
                  onClick={() =>
                    navigate(
                      p.quotes[0]
                        ? `/quotes/${p.quotes[0].id}`
                        : `/quotes/new?project=${id}&customer=${p.customer_id || ""}` +
                            `&address=${encodeURIComponent(p.job_address || "")}` +
                            `&name=${encodeURIComponent(p.name || "")}`,
                    )
                  }
                  className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
                >
                  <Plus size={13} weight="bold" /> Open drawing
                </button>
              }
            >
              {(p.drawings || []).every(
                (d) => (d.areas || []).length === 0,
              ) && (
                <p className="text-xs text-zinc-500">
                  No drawings on the quotes yet.
                </p>
              )}
              {(p.drawings || []).map((d) =>
                (d.areas || []).map((a) => (
                  <div
                    key={`${d.quote_id}-${a.id}`}
                    className="mb-4"
                    data-testid={`project-drawing-${a.id}`}
                  >
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      <span className="font-mono-data text-indigo-300">
                        {d.quote_code}
                      </span>
                      {a.name}
                    </div>
                    <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <DesignerPlan pieces={a.pieces || []} light />
                    </div>
                  </div>
                )),
              )}
            </Card>

            <Card
              title="Files"
              icon={FileText}
              testid="project-files-card"
              show={tab === "files"}
              action={
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    data-testid="project-file-input"
                    onChange={(e) => upload(e.target.files?.[0])}
                  />
                  <Button
                    data-testid="project-file-upload"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    className="h-9 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest"
                  >
                    <UploadSimple size={13} weight="bold" className="mr-1.5" />{" "}
                    Add file
                  </Button>
                </>
              }
            >
              {(p.files || []).length === 0 ? (
                <p className="text-xs text-zinc-500">No files yet.</p>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {p.files.map((f) => (
                    <a
                      key={f.id}
                      href={`${API}/files/${f.storage_path}?auth=${localStorage.getItem("sw_token")}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`project-file-${f.id}`}
                      className="flex items-center justify-between gap-3 py-2.5 text-sm text-zinc-300 hover:text-white"
                    >
                      <span className="min-w-0 flex-1 break-words sm:truncate">{f.original_filename}</span>
                      <span className="font-mono-data text-[11px] text-zinc-500">
                        {Math.round((f.size || 0) / 1024)} KB
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div className="space-y-4">
            <Card
              title="Tasks"
              icon={CheckSquare}
              testid="project-tasks-card"
              show={tab === "tasks"}
              action={
                <button
                  type="button"
                  data-testid="project-add-task"
                  onClick={openAddTask}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:border-indigo-500 hover:text-indigo-300"
                >
                  <Plus size={14} weight="bold" />
                </button>
              }
            >
              <div className="space-y-2" data-testid="project-tasks-list">
                {(p.tasks || []).length === 0 && (
                  <p className="text-xs text-zinc-500">
                    No tasks yet — add a templating, fabrication or install task.
                  </p>
                )}
                {(p.tasks || []).map((t) => {
                  const overdue = !t.done && t.date && t.date < todayIso;
                  return (
                  <div
                    key={t.id}
                    data-testid={`project-task-${t.id}`}
                    data-overdue={overdue ? "1" : "0"}
                    className={`rounded-md border bg-zinc-950 p-3 ${
                      overdue ? "border-red-500/40" : "border-zinc-800"
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        data-testid={`project-task-toggle-${t.id}`}
                        onClick={() => toggleTaskDone(t.id)}
                        title="Mark done"
                        className="shrink-0"
                      >
                        {t.done ? (
                          <CheckSquare
                            size={18}
                            weight="fill"
                            className="text-emerald-400"
                          />
                        ) : (
                          <Square
                            size={18}
                            weight="bold"
                            className="text-zinc-600"
                          />
                        )}
                      </button>
                      {t.kind && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-200">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: t.color || "#71717a" }}
                          />
                          {t.kind}
                        </span>
                      )}
                      <span
                        className={`text-sm ${
                          t.done
                            ? "text-zinc-500 line-through"
                            : "text-zinc-100"
                        }`}
                      >
                        {t.label || t.title || t.kind}
                      </span>
                      {t.date && (
                        <span
                          className={`font-mono-data inline-flex items-center gap-1 text-xs ${
                            overdue ? "text-red-300" : "text-zinc-400"
                          }`}
                        >
                          <CalendarBlank size={13} /> {t.date}
                          {t.time ? ` · ${t.time}` : ""}
                        </span>
                      )}
                      {overdue && (
                        <span
                          data-testid={`project-task-overdue-${t.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-red-300"
                        >
                          <Warning size={10} weight="fill" /> Overdue
                        </span>
                      )}
                      <span className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-400">
                        <User size={13} /> {crewLabel(t.crew)}
                      </span>
                      <button
                        type="button"
                        data-testid={`project-task-edit-${t.id}`}
                        onClick={() => openEditTask(t)}
                        title="Edit task"
                        className="text-zinc-500 transition-colors duration-150 hover:text-indigo-300"
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button
                        type="button"
                        data-testid={`project-task-delete-${t.id}`}
                        onClick={() => deleteTask(t.id)}
                        title="Remove task"
                        className="text-zinc-500 transition-colors duration-150 hover:text-red-400"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                    {(t.areas?.length > 0 ||
                      (t.custom_fields || []).some((c) => c.label || c.value) ||
                      t.notes?.length > 0) && (
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400">
                        {t.areas?.length > 0 && (
                          <span data-testid={`project-task-areas-${t.id}`}>
                            Areas: {t.areas.join(", ")}
                          </span>
                        )}
                        {(t.custom_fields || [])
                          .filter((c) => c.label || c.value)
                          .map((c, i) => (
                            <span key={i}>
                              {c.label}:{" "}
                              <span className="text-zinc-300">{c.value}</span>
                            </span>
                          ))}
                        {t.notes?.length > 0 && (
                          <span className="text-zinc-500">
                            {t.notes.length} note
                            {t.notes.length === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </Card>

            <TaskDialog
              open={taskDialog.open}
              onOpenChange={(v) => setTaskDialog((s) => ({ ...s, open: v }))}
              resourcePath={`/projects/${id}/tasks`}
              areasSource={`/projects/${id}/drawing-areas`}
              task={taskDialog.task}
              taskTypes={taskTypes}
              crewList={crewList}
              onSaved={(data) => setP(data)}
            />

            {tab === "comms" && (
            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="project-messages-card"
            >
              <div className="flex overflow-hidden rounded-md border border-zinc-800">
                {[
                  ["portal", "Customer"],
                  ["emails", "Emails"],
                  ["notes", "Team Notes"],
                  ["activity", "Activity"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`project-messages-tab-${k}`}
                    onClick={() => setMsgTab(k)}
                    className={`h-11 flex-1 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                      msgTab === k
                        ? "bg-indigo-500 text-white"
                        : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {msgTab === "portal" ? (
                <div className="mt-3 [&>div]:border-0 [&>div]:bg-transparent [&>div]:p-0">
                  <PortalThread kind="project" recordId={id} />
                </div>
              ) : msgTab === "emails" ? (
                <JobEmailThread
                  jobIds={(p.jobs || []).map((j) => j.id)}
                  composeJobId={(p.jobs || [])[0]?.id}
                  testId="project-email-thread"
                />
              ) : msgTab === "notes" ? (
                <TeamNotes p={p} id={id} setP={setP} />
              ) : (
                <div
                  className="mt-3 space-y-2"
                  data-testid="project-activity-tab"
                >
                  {(p.activity || []).length === 0 && (
                    <p className="text-xs text-zinc-500">Nothing logged yet.</p>
                  )}
                  {(p.activity || []).map((a, i) => (
                    <div
                      key={`${a.at}-${i}`}
                      className="border-l-2 border-zinc-800 pl-3 text-xs"
                    >
                      <div className="text-zinc-300">{a.text}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-600">
                        {day(a.at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </main>
      <LabelDialog
        jobId={labelDlg?.jobId || ""}
        kind={labelDlg?.kind || "Install"}
        open={!!labelDlg}
        onOpenChange={(o) => !o && setLabelDlg(null)}
      />
    </div>
  );
}
